import { Alert, Button, Divider, Paper, Stack, Typography } from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { confirm } from "lib/@dialogs/confirm"
import { renameMacro } from "authoring/routes/macro/controller/rename-macro"
import { updateMacroContent } from "authoring/routes/macro/controller/update-macro-content"
import { deleteMacro } from "authoring/routes/macro/controller/delete-macro"
import { getMacroConsumers } from "authoring/routes/macro/controller/get-macro-consumers"
import { buildMacroCategoryList } from "authoring/routes/macro/lib/macro-category-utils"
import { MACRO_NAME_ERROR, isValidMacroName, normalizeMacroName } from "authoring/routes/macro/lib/macro-utils"
import { formatMacroDateTime } from "authoring/routes/macro/lib/macro-formatters"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { EditRecord } from "lib/@components/edit-record"
import { MacroCategoriesEditor } from "./macro-categories-editor"
import { LinkedMacrosEditor } from "./linked-macros-editor"
import { MacroConsumersList } from "./macro-consumers-list"
import { MacroHistoryList } from "./macro-history-list"
import { MacroEditorHeader } from "./macro-editor-header"
import { MacroContentEditor } from "./macro-content-editor"
import { MacroContentView } from "./macro-content-view"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import { AUTHORING_MANAGE_MACROS_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function MacroEditorPanel({ macro, macros = [], editMode = true, onArchive }) {
    const [nameInput, setNameInput] = useState("")
    const [isEditingName, setIsEditingName] = useState(false)
    const [savingName, setSavingName] = useState(false)
    const [archiving, setArchiving] = useState(false)

    const { data: consumers, loading: consumersLoading } = getMacroConsumers.useResults.status({ macroId: macro?.id })
    const categoryOptions = useMemo(() => buildMacroCategoryList(macros), [macros])
    const referenceCount = useMemo(() => countReferences(macros, macro?.id), [macro?.id, macros])
    const linkedCount = useMemo(() => (Array.isArray(macro?.linked) ? macro.linked.length : 0), [macro?.linked])
    const consumerCount = useMemo(() => (Array.isArray(consumers) ? consumers.length : 0), [consumers])

    const normalizedName = useMemo(() => normalizeMacroName(nameInput), [nameInput])
    const normalizedCurrentName = useMemo(() => normalizeMacroName(macro?.name ?? ""), [macro?.name])
    const isNameDirty = normalizedName !== normalizedCurrentName

    const deleteCaption = getDeleteCaption(referenceCount)
    const deleteMessage = getDeleteMessage(referenceCount, linkedCount)
    const consumerWarning = getConsumerWarning(consumerCount)

    useEffect(() => {
        setNameInput(macro?.name ?? "")
        setIsEditingName(false)
    }, [macro?.id, macro?.name])

    if (!macro) return null

    return (
        <Paper sx={{ p: 2.5, flex: 1, minHeight: 0, overflowY: "auto" }} data-cy="macro-editor-panel">
            <Stack spacing={2.25}>
                <MacroEditorHeader
                    macro={macro}
                    editMode={editMode}
                    isEditingName={isEditingName}
                    nameInput={nameInput}
                    savingName={savingName}
                    isNameDirty={isNameDirty}
                    onStartEdit={startNameEdit}
                    onNameChange={handleNameChange}
                    onConfirmEdit={handleConfirmNameEdit}
                    onCancelEdit={handleCancelNameEdit}
                />

                <Divider />

                <Stack spacing={1.25} data-cy="macro-content-section">
                    <Typography variant="subtitle2">Content</Typography>
                    <If truthy={editMode}>
                        <EditRecord key={macro.id} target={macro} store={saveContent} debounceTime={1000}>
                            <MacroContentEditor />
                        </EditRecord>
                        <Else>
                            <MacroContentView content={macro?.content} />
                        </Else>
                    </If>
                    <Typography variant="caption" color="text.secondary">
                        Last modified {formatMacroDateTime(macro?.modified)}
                    </Typography>
                </Stack>

                <Divider />

                <MacroCategoriesEditor macro={macro} categories={categoryOptions} editMode={editMode} />

                <Divider />

                <LinkedMacrosEditor macro={macro} macros={macros} editMode={editMode} />

                <Divider />

                <MacroConsumersList consumers={consumers} loading={consumersLoading} />
                <Secure demand={AUTHORING_MANAGE_MACROS_DEMAND} fallback={<Frag />}>
                    <If truthy={!!consumerWarning && editMode}>
                        <Alert severity="warning" data-cy="macro-consumer-warning">
                            {consumerWarning}
                        </Alert>
                    </If>

                    <Divider />
                </Secure>

                <MacroHistoryList history={macro?.history ?? []} currentVersion={macro?.version} />

                <Secure demand={AUTHORING_MANAGE_MACROS_DEMAND} fallback={<Frag />}>
                    <If truthy={editMode}>
                        <Stack spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                                {deleteMessage}
                            </Typography>
                            <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={handleArchive}
                                disabled={archiving}
                                sx={{ alignSelf: "flex-start" }}
                                data-cy="macro-delete-button"
                            >
                                {deleteCaption}
                            </Button>
                        </Stack>
                    </If>
                </Secure>
            </Stack>
        </Paper>
    )

    function startNameEdit() {
        setIsEditingName(true)
    }

    function handleNameChange(event) {
        setNameInput(event.target.value)
    }

    function handleCancelNameEdit() {
        setNameInput(macro?.name ?? "")
        setIsEditingName(false)
    }

    async function handleConfirmNameEdit() {
        setIsEditingName(false)
        if (!macro?.id) return
        if (!isNameDirty) return

        const validationError = getNameError(nameInput)
        if (validationError) {
            errorSnackbar(validationError)
            return
        }

        if (!normalizedName) {
            errorSnackbar("Macro name is required")
            return
        }

        try {
            setSavingName(true)
            const updated = await renameMacro({ id: macro.id, newName: normalizedName })
            if (updated) {
                successSnackbar("Macro renamed")
                setNameInput(normalizedName)
                return
            }
            errorSnackbar("Failed to rename macro")
        } catch (error) {
            console.error(error)
            errorSnackbar(error?.message || "Failed to rename macro")
        } finally {
            setSavingName(false)
        }
    }

    async function saveContent({ content }) {
        if (!editMode) return
        try {
            await updateMacroContent({ id: macro.id, content })
        } catch (error) {
            errorSnackbar(error?.message || "Failed to save content")
        }
    }

    async function handleArchive() {
        if (!macro?.id) return
        try {
            const confirmArchive = await confirm(deleteMessage, deleteCaption)
            if (!confirmArchive) return
            setArchiving(true)
            const archived = await deleteMacro({ id: macro.id })
            if (archived) {
                successSnackbar("Macro archived")
                if (onArchive) onArchive(macro)
                return
            }
            errorSnackbar("Failed to archive macro")
        } catch (error) {
            console.error(error)
            errorSnackbar(error?.message || "Failed to archive macro")
        } finally {
            setArchiving(false)
        }
    }
}

function countReferences(macros = [], macroId) {
    if (!macroId) return 0
    return macros.filter((macro) => {
        if (!macro || macro._deleted) return false
        if (macro.id === macroId) return false
        const linked = Array.isArray(macro.linked) ? macro.linked : []
        return linked.includes(macroId)
    }).length
}

function getDeleteCaption(referenceCount) {
    if (referenceCount > 0) {
        return `Delete across all macros (${referenceCount})`
    }
    return "Delete Macro"
}

function getDeleteMessage(referenceCount, linkedCount) {
    const messages = []
    if (referenceCount > 0) {
        messages.push(
            `${referenceCount} macros reference this macro in linked suggestions. These links will be removed.`
        )
    }
    if (linkedCount > 0) {
        const label = linkedCount === 1 ? "macro" : "macros"
        messages.push(`This macro links to ${linkedCount} ${label}. Those links will be removed.`)
    }
    if (!messages.length) {
        return "This will remove the macro from active lists."
    }
    return messages.join(" ")
}

function getConsumerWarning(consumerCount) {
    if (!consumerCount) return ""
    const label = consumerCount === 1 ? "consumer" : "consumers"
    return `This macro is used by ${consumerCount} ${label}. Deleting it will render its token invalid.`
}

function getNameError(value) {
    if (!value?.trim()) return ""
    if (!isValidMacroName(value)) return MACRO_NAME_ERROR
    return ""
}
